import { useEffect, useMemo, useState } from "react"

import { resolveBatchDataEntry, useBatchDataMap } from "@/lib/batch-data-context"
import { buildTamdaMediaNewsSidebarKey } from "@/lib/extract-batch-requests"
import { PostData } from "@/types/types"
import { CategoryConfig } from "../types/config"
import { Article } from "../types/new"
import { mapPostToArticle } from "../utils/article"
import { createCategoriesSignature, normalizeCategories } from "../utils/category"

type SidebarTab = {
  categoryId: string
  categoryName: string
  articles: Article[]
  postsData?: Map<string, PostData>
}

type UseNewsSidebarResult = {
  sidebar: SidebarTab[]
  loading: boolean
}

const buildFallbackSidebar = (categoryId: string, categoryName: string): SidebarTab => ({
  categoryId,
  categoryName,
  articles: [],
})

const buildSidebarFromPosts = (category: { id: string; name: string }, posts: PostData[]): SidebarTab => {
  const postsDataMap = new Map<string, PostData>()
  posts.forEach(post => {
    postsDataMap.set(post.id.toString(), post)
  })

  return {
    categoryId: category.id,
    categoryName: category.name,
    articles: posts.map(mapPostToArticle),
    postsData: postsDataMap,
  }
}

export const useNewsSidebar = (
  categoriesConfig: CategoryConfig[] = [],
  componentId?: string,
): UseNewsSidebarResult => {
  const normalizedCategories = useMemo(() => normalizeCategories(categoriesConfig), [categoriesConfig])
  const categoriesSignature = useMemo(() => createCategoriesSignature(normalizedCategories), [normalizedCategories])
  const batchData = useBatchDataMap()
  const [sidebar, setSidebar] = useState<SidebarTab[]>([])
  const [loading, setLoading] = useState(false)

  const prefetchedSidebar = useMemo(
    () =>
      normalizedCategories.map(category => {
        if (!componentId) {
          return undefined
        }

        const response = resolveBatchDataEntry(batchData, {
          key: buildTamdaMediaNewsSidebarKey(componentId, category.id),
          prefix: "tamda-media-news-layout:",
          suffix: `:sidebar:${category.id}`,
        })
        const posts = Array.isArray(response?.data) ? (response.data as PostData[]) : undefined

        if (!posts) {
          return undefined
        }

        return buildSidebarFromPosts(category, posts)
      }),
    [batchData, componentId, normalizedCategories],
  )

  useEffect(() => {
    if (normalizedCategories.length === 0) {
      setSidebar([])
      setLoading(false)
      return
    }

    const hasMissingPrefetch = prefetchedSidebar.some(item => !item)

    setSidebar(
      prefetchedSidebar.map((item, index) => {
        if (item) {
          return item
        }

        const category = normalizedCategories[index]
        return buildFallbackSidebar(category.id, category.name)
      }),
    )
    setLoading(hasMissingPrefetch)
  }, [categoriesSignature, normalizedCategories, prefetchedSidebar])

  return {
    sidebar,
    loading,
  }
}
